import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineClock, HiOutlineStar } from 'react-icons/hi';
import { HiOutlineTrophy } from 'react-icons/hi2';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { shortAddress } from '../services/wallet';
import { TeamSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';
import { rankTitle } from '../utils/ranks';

function money(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function legPercent(volume, target) {
  const value = Number(volume) || 0;
  const need = Number(target) || 0;
  if (need <= 0) return value > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, (value / need) * 100));
}

function LegProgress({ label, volume, target, tone }) {
  const filled = legPercent(volume, target);
  const remaining = Math.max(0, 100 - filled);
  const showFillLabel = filled >= 12;
  const showRemainLabel = remaining >= 18;
  return (
    <div className={`rank-leg rank-leg--${tone}`}>
      <div className="rank-leg__head">
        <strong>{label}</strong>
        <span>{money(volume)} / {money(target)} USDT</span>
      </div>
      <div
        className="rank-leg__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(filled)}
        aria-label={`${label} progress`}
      >
        <div className="rank-leg__fill" style={{ width: `${filled}%` }}>
          {showFillLabel ? <em>{filled.toFixed(0)}%</em> : null}
        </div>
        <div className="rank-leg__rest">
          {showRemainLabel ? <span>Remaining {remaining.toFixed(0)}%</span> : null}
        </div>
      </div>
    </div>
  );
}

function growthLine(growth, lockedAmount) {
  const locked = Number(lockedAmount) || 0;
  const held = locked > 0 ? ` ${money(locked)} USDT is locked.` : '';
  if (!growth) return `Weekly bonus stays locked until business growth is known.${held}`;
  const need = `${growth.required_percent}% in ${growth.days} days`;
  if (growth.baseline == null) return `No ${growth.days}-day history yet. Bonus stays locked until then.${held}`;
  if (growth.from_zero && growth.open) {
    return `Business grew from 0. Need ${need}. Locked bonus is paid with this week.`;
  }
  const rate = Number(growth.growth_percent || 0).toFixed(2);
  return growth.open
    ? `Business grew ${rate}%. Need ${need}. Locked bonus is paid with this week.`
    : `Business grew ${rate}%. Need ${need}. This week stays locked.${held}`;
}

function when(iso) {
  if (!iso) return 'Not yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Not yet';
  return date.toLocaleString();
}

export default function RankPage() {
  const { user, openConnect, connectOpening, loading } = useWallet();
  const toast = useToast();
  const [rank, setRank] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setRank(null);
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    api
      .myRank()
      .then((data) => {
        if (!cancelled) setRank(data.rank);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.message || 'Failed to load rank');
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, loading, toast]);

  if (loading || (user && pageLoading)) {
    return <TeamSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Rank" subtitle="Team business and rewards" />
        <motion.div className="panel panel--glow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <h2>Connect to see your rank</h2>
          <button
            className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
            type="button"
            onClick={() => openConnect('Connect wallet to view rank')}
            disabled={connectOpening}
            aria-busy={connectOpening}
          >
            {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
            {connectOpening ? 'Connecting…' : 'Connect Wallet'}
          </button>
        </motion.div>
      </>
    );
  }

  const live = rank?.live;
  const profile = rank?.profile;
  const thresholds = rank?.thresholds || [];
  const liveRank = Number(live?.projected_rank ?? 0);
  const nextStep = thresholds.find((step) => Number(step.rank) === liveRank + 1);
  const currentStep = thresholds.find((step) => Number(step.rank) === liveRank);
  const targetAmount = Number(nextStep?.amount ?? currentStep?.amount ?? thresholds[0]?.amount ?? 0);
  const targetLabel = nextStep
    ? `Progress to ${rankTitle(nextStep.rank)}`
    : liveRank > 0
      ? `Maintaining ${rankTitle(liveRank)}`
      : 'Progress to Explorer';

  return (
    <>
      <PageHeader title="Rank" subtitle="Team business and rewards" />

      <div className="rank-page">
      <div className="grid dash-stats">
        <motion.div className="stat dash-tile dash-tile--gold" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span><HiOutlineStar /> Profile rank</span>
          <strong>{rankTitle(profile?.rank ?? 0)}</strong>
        </motion.div>
        <motion.div className="stat dash-tile dash-tile--cyan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span>Saved business</span>
          <strong>{money(profile?.business)}<em>USDT</em></strong>
        </motion.div>
        <motion.div className="stat dash-tile dash-tile--green" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span>Live achievable</span>
          <strong>{money(live?.achievable)}<em>USDT</em></strong>
        </motion.div>
        <motion.div className="stat dash-tile dash-tile--violet" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span>Live rank</span>
          <strong>{rankTitle(live?.projected_rank ?? 0)}</strong>
        </motion.div>
      </div>

      <motion.section className="panel rank-live" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="rank-live__title">
          <h2>Live Rank Status</h2>
          <HiOutlineTrophy aria-hidden="true" />
        </div>
        <p className="muted rank-live__hint">
          {targetLabel}. Qualifying business is the smaller of the two legs ({money(targetAmount)} USDT needed).
        </p>
        <div className="rank-live__legs">
          <LegProgress
            label="Strong Leg"
            volume={live?.strong_volume}
            target={targetAmount}
            tone="strong"
          />
          <LegProgress
            label="Other Leg"
            volume={live?.other_volume}
            target={targetAmount}
            tone="other"
          />
        </div>
      </motion.section>

      <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h2><HiOutlineClock /> Schedule</h2>
        <p className="muted">Profile updates daily at {rank?.refresh_time || '00:00'}.</p>
        <p>Next refresh: <strong>{when(rank?.next_refresh_at)}</strong></p>
        <p className="muted">
          Bonus {rank?.bonus_weekday_label || 'Monday'} {rank?.bonus_time || '00:00'} · next {when(rank?.next_bonus_at)}
        </p>
        <p className="muted">
          {growthLine(rank?.bonus_growth, (rank?.plans || []).reduce((sum, step) => sum + Number(step.locked_amount || 0), 0))}
        </p>
      </motion.section>

      <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h2>Leg requirements</h2>
        <div className="rank-split">
          <div>
            <span>{rankTitle(live?.team_gate?.fromRank ?? 7)}–{rankTitle(10)}</span>
            <strong>{live?.team_gate?.haveLegs ?? 0}/{live?.team_gate?.needLegs ?? 3} teams</strong>
            <em>Each direct team needs {live?.team_gate?.needUsers ?? 3} users at {rankTitle(live?.team_gate?.minRank ?? 1)}+</em>
          </div>
        </div>
        <div className="rank-split rank-split--3">
          {(live?.gates || []).map((gate) => (
            <div key={gate.rank}>
              <span>{rankTitle(gate.rank)}</span>
              <strong>{gate.haveLegs}/{gate.needLegs} legs</strong>
              <em>Need {rankTitle(gate.needRank)} on different legs</em>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h2>Direct legs</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Team deposits</th>
                <th>Side</th>
                <th>Best rank in leg</th>
                <th>Ranked users</th>
              </tr>
            </thead>
            <tbody>
              {(live?.legs || []).map((leg) => (
                <tr key={leg.user_id} className={leg.is_strong ? 'is-current' : undefined}>
                  <td className="mono" title={leg.wallet_address}>{shortAddress(leg.wallet_address)}</td>
                  <td>{money(leg.volume)} USDT</td>
                  <td>{leg.is_strong ? 'Strong team' : 'Other team'}</td>
                  <td>{rankTitle(leg.best_rank ?? 0)}</td>
                  <td>{leg.ranked_users ?? 0}</td>
                </tr>
              ))}
              {!live?.legs?.length ? (
                <tr>
                  <td colSpan={5} className="muted">No direct legs yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </motion.section>

      <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h2>Ranks and weekly rewards</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Matched business</th>
                <th>Weekly</th>
                <th>Max</th>
                <th>Paid</th>
                <th>Locked</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(rank?.plans || thresholds).map((step) => (
                <tr key={step.rank} className={Number(profile?.rank) === step.rank ? 'is-current' : undefined}>
                  <td>{rankTitle(step.rank)}</td>
                  <td>{money(step.amount)} USDT</td>
                  <td>{step.weekly == null ? '—' : money(step.weekly)}</td>
                  <td>{step.max == null ? '—' : money(step.max)}</td>
                  <td>{step.total_paid == null ? '—' : `${money(step.total_paid)} (${step.weeks_paid || 0}w)`}</td>
                  <td>{money(step.locked_amount || 0)}</td>
                  <td>
                    {step.completed_at
                      ? 'Completed'
                      : !step.unlocked
                        ? 'Locked'
                        : rank?.bonus_growth?.open
                          ? 'Paying weekly'
                          : 'Bonus locked'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </motion.section>
      </div>
    </>
  );
}
